import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Table } from 'components/molecules/Table';
import { Pagination } from 'components/molecules/Pagination';
import { useAlert } from 'models/alertContext';
import { EPerPages, ISortOptions } from 'models/table';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid, Tooltip } from '@mui/material';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ILicencesSearchForm } from 'models/licences';
import {
  customFieldsPrepareToSearch,
  objectToQueryString,
  prepareDataWithCustomFieldsToSearch,
  prepareQueryValues,
} from 'helpers/searchQuery';
import { CardList } from 'components/molecules/CardList';
import { formatOnlyDateToRequest } from 'utils/date';
import { replaceUrlIfOnPage } from 'utils/history';
import { ERouteLinks } from 'models/route';
import { renderLicenceRowColor } from 'components/molecules/Table/helpers';
import { ISearchFormValues } from 'models/licence';
import { isExportNotAvailable } from 'constants/Export';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EPermission } from 'models/permissions';
import { exportLicenceCSV } from 'requests/report';
import { getGlobalPropertyByKey } from 'requests/globalProperty';
import { useLicences } from './hooks';
import { initialSearchFormValues, licencesTableColumns, mapToDataSource } from './helpers';
import { LicenceSearchForm } from './SearchForm';
import { LicenceTableCard } from './LicenceTableCard';
import Button from '../../components/atoms/Button';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { useLang } from 'models/langContext';
import { ECustomFieldObjectType, ICustomFieldByObjectType } from 'models/customField';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { ETabId } from '.';

export interface ISearchParams {
  [key: string]:
    | string
    | Date
    | number
    | boolean
    | string[]
    | ISearchFormCustomFieldValue
    | ISearchFormCustomFieldValue[];
}

const LicencesPage = () => {
  const { pathname }: any = useLocation();
  const { nextPageAlert, clearAlert } = useAlert();
  const navigate = useNavigate();
  const { licences, getLicencesSearchData, paginator, weaponLicencingAuthority } =
    useLicences();
  const [isSearchParams, setIsSearchParams] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [licenceQuery, setLicenceQuery] = useState<Partial<ILicencesSearchForm>>({});
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const [isLoader, setLoader] = useState(true);
  const [initialValues, setInitialValues] = useState<ILicencesSearchForm | null | undefined>(
    null,
  );
  const [searchParams] = useSearchParams();
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isExportLoading, setExportLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [leftDaysNotify, setLeftDaysNotify] = useState<number>(0);
  const { selectedLanguage } = useLang();
  const { getCustomField, customFieldsLicence } = useLegalEntities();

  useEffect(() => {
    (async function init() {
      await getCustomField(ECustomFieldObjectType.LICENCE);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const scrollPosition = useRef(0);

  useEffect(() => {
    if (!isLoader) clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
  }, [isLoader]);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async function getGlobalPropertyDataInit() {
      const leftDays = await getGlobalPropertyByKey(
        'licence_expiration_notification_days_before',
      );
      if (leftDays) {
        setLeftDaysNotify(Number(leftDays.value));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousSortRef = useRef(sort);

  useEffect(() => {
    (async function getLicencesDataInit() {
      if (Object.keys(licenceQuery).length) {
        if (sort !== previousSortRef.current) {
          setIsSortLoaded(false);
        }
        if (!licences?.length) {
          setLoader(true);
        }
        customFieldsPrepareToSearch(licenceQuery.customFields);
        const componentsQueryData = prepareDataWithCustomFieldsToSearch(
          licenceQuery,
          customFieldsLicence.filter((item: ICustomFieldByObjectType) => item.searchCriteria),
        );

        await getLicencesSearchData(currentPage, perPage, { ...componentsQueryData }, sort);
        setLoader(false);
        setIsSortLoaded(true);
        previousSortRef.current = sort;
      }
    })();
  }, [currentPage, perPage, licenceQuery, sort]);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  useEffect(() => {
    setIsPaginationLoading(true);

    const query = new URLSearchParams(searchParams);
    const queryValues: ISearchParams =
      prepareQueryValues(initialSearchFormValues, query) ?? {};

    const formValues: ILicencesSearchForm = {
      ...initialSearchFormValues,
      ...queryValues,
    };

    setInitialValues(formValues);

    const nextPageParam = Number(query.get('currentPage')) || currentPage;
    const perPageParam = Number(query.get('perPage')) || perPage;

    const initialQuery: Partial<ILicencesSearchForm> = {
      legalName: formValues.legalName,
      legalTypeUniqueId: formValues.legalTypeUniqueId,
      statusesUniqueIds: formValues.statusesUniqueIds,
      licenceAuthorityUniqueIds: formValues.licenceAuthorityUniqueIds,
      expiryDateFrom: formatOnlyDateToRequest(formValues.expiryDateFrom) || null,
      expiryDateTo: formatOnlyDateToRequest(formValues.expiryDateTo) || null,
    };
    if (queryValues && Object.keys(queryValues).length) {
      setIsSearchParams(true);
    }
    setCurrentPage(nextPageParam);
    setPerPage(perPageParam);
    setLicenceQuery(initialQuery);
    setIsPaginationLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const getNewQueryPath = (values: Partial<ILicencesSearchForm>) => {
    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;

    const expiryDateFrom = values.expiryDateFrom
      ? formatOnlyDateToRequest(values.expiryDateFrom)
      : '';
    const expiryDateTo = values.expiryDateTo
      ? formatOnlyDateToRequest(values.expiryDateTo)
      : '';
    const statusesUniqueIds = joinValuesToString(values.statusesUniqueIds) ?? '';
    const licenceAuthorityUniqueIds =
      joinValuesToString(values.licenceAuthorityUniqueIds) ?? '';
    const queryString = objectToQueryString(
      {
        ...values,
        expiryDateFrom,
        expiryDateTo,
        statusesUniqueIds,
        licenceAuthorityUniqueIds,
        ...{
          perPage: perPage,
          currentPage: currentPage,
        },
      },
      customFieldsLicence,
    );

    if (queryString) {
      return `${pathname}?${queryString}&expand=${ETabId.licences}`;
    }
    return pathname;
  };

  const handleSetLicenceQuery = (values: Partial<ILicencesSearchForm>) => {
    const { invokedByPageChange, preset, ...rest } = values;

    const queryData = {
      expiryDateFrom: formatOnlyDateToRequest(rest.expiryDateFrom) || null,
      expiryDateTo: formatOnlyDateToRequest(rest.expiryDateTo) || null,
      weaponLicenceName: rest.weaponLicenceName,
      weaponLicenceNumber: rest.weaponLicenceNumber,
      legalName: rest.legalName,
      legalUniqueId: rest.legalUniqueId,
      legalTypeUniqueId: rest.legalTypeUniqueId,
      statusesUniqueIds: rest.statusesUniqueIds,
      licenceAuthorityUniqueIds: rest.licenceAuthorityUniqueIds,
      customFields: rest.customFields,
    };

    replaceUrlIfOnPage(getNewQueryPath(queryData));

    if (!isInitialLoad) {
      const filtersChanged =
        queryData.legalName !== licenceQuery?.legalName ||
        queryData.legalTypeUniqueId !== licenceQuery?.legalTypeUniqueId ||
        queryData.statusesUniqueIds !== licenceQuery?.statusesUniqueIds ||
        queryData.licenceAuthorityUniqueIds !== licenceQuery?.licenceAuthorityUniqueIds ||
        queryData.expiryDateFrom !== licenceQuery?.expiryDateFrom ||
        queryData.expiryDateTo !== licenceQuery?.expiryDateTo;

      if (filtersChanged && !invokedByPageChange) {
        setCurrentPage(1);
      }
    } else {
      setIsInitialLoad(false);
    }
    setLicenceQuery(queryData);
  };

  const navigateToDetail = (id: string, type: string) => {
    navigate(`${ERouteLinks.DetailLicence.replace(':licenceId', id)}?type=${type}`);
  };

  const exportCsv = async () => {
    setExportLoading(true);
    await exportLicenceCSV(licenceQuery, sort);
    setExportLoading(false);
  };

  return (
    <>
      {nextPageAlert && (
        <Box sx={{ mb: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}

      {initialValues && (
        <LicenceSearchForm
          onSubmitForm={handleSetLicenceQuery}
          initialSearchForm={initialValues}
          isSearchButtonDisabled={isLoader}
          isSearchParams={isSearchParams}
          getNewQueryPath={getNewQueryPath}
          perPage={perPage}
          currentPage={currentPage}
          customFieldsLicence={customFieldsLicence}
        />
      )}

      <Grid
        size={{ xs: 12 }}
        container
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '10px 0',
          gap: 1,
        }}
      >
        {isExportNotAvailable(paginator) ? (
          <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
            <span>
              <Button
                variant={EButtonVariants.outlined}
                size={EButtonSizes.medium}
                permission={[EPermission.EXPORT_LICENCES]}
                label={t('licences.export.button')}
                onClick={exportCsv}
                isLoading={isExportLoading}
                disabled
              />
            </span>
          </Tooltip>
        ) : (
          <Button
            variant={EButtonVariants.outlined}
            size={EButtonSizes.medium}
            permission={[EPermission.EXPORT_LICENCES]}
            label={t('licences.export.button')}
            onClick={exportCsv}
            isLoading={isExportLoading}
          />
        )}
      </Grid>

      {isLoader ? (
        <Loader isLoading={isLoader} />
      ) : (
        <div>
          {isDesktop ? (
            <Table
              columns={licencesTableColumns({
                customFields: customFieldsLicence,
                selectedLanguage: selectedLanguage?.uniqueId,
                dateFormat: shortDateFormat,
                t,
                leftDaysNotify,
              })}
              dataSource={mapToDataSource(licences ?? [], weaponLicencingAuthority)}
              onSort={(it) => {
                setSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              specificRowColor={renderLicenceRowColor}
              specificRowColorHover={renderLicenceRowColor}
              loading={isLoader}
            />
          ) : (
            <CardList
              items={mapToDataSource(licences ?? [], weaponLicencingAuthority)}
              render={(data, index) => (
                <LicenceTableCard
                  data={data}
                  shortDateFormat={shortDateFormat}
                  navigateToDetail={navigateToDetail}
                  willExpireLimitInfo={leftDaysNotify}
                  customFields={customFieldsLicence}
                  selectedLanguage={selectedLanguage?.uniqueId}
                  key={index}
                />
              )}
            />
          )}
        </div>
      )}
      {paginator && (
        <Pagination
          count={paginator?.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={isPaginationLoading}
          isVisible={isSortLoaded}
        />
      )}
    </>
  );
};

export default LicencesPage;
